"use client"

import { Zap, Music } from "lucide-react"


export default function UsagePage() {

    return (
        <>
            {/* Podcasting Section */}
            <section className="py-8 sm:py-12 lg:py-16 px-6 sm:px-8 lg:px-32 relative bg-white">
                <div className="container mx-auto max-w-7xl relative z-10">
                    {/* Block 1: Text left, Image right */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start mb-12">
                        <div className="order-2 lg:order-1">
                            <h2 className="onest-heading-main mb-4 sm:mb-6 text-gray-900">
                                Подкастинг
                            </h2>
                            <p className="onest-description text-gray-600 mb-6 sm:mb-8">
                                С нашим простым в использовании веб-инструментом вы сможете вывести качество своего подкаста на новый уровень. Вы загружаете свой выпуск, а наши алгоритмы искусственного интеллекта делают всё остальное.
                            </p>
                            <h3 className="onest-feature-title text-gray-900 mb-4 sm:mb-6">
                                Мои преимущества как подкастера:
                            </h3>
                            <div className="space-y-3 sm:space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Улучшите качество вашего подкаста, сбалансировав уровни между динамиками и используя нормализацию громкости.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Устранение нежелательных шумов и шипения, чтобы создать теплое и приятное звучание</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Улучшите качество прослушивания, создав улучшенные подкасты с главами и транскрипциями.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Автоматически публикуйте аудио- и видеоверсии на хостингах и платформах подкастов.</p>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <img
                                src="/usage_1-1280.webp"
                                srcSet="/usage_1-640.webp 640w, /usage_1-1280.webp 1280w"
                                sizes="(min-width: 1024px) 600px, 100vw"
                                width={1280}
                                height={1129}
                                alt="Микрофон с карточкой 'Как улучшить подкаст'"
                                className="w-full h-auto rounded-2xl"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>

                    {/* Block 2: Image left, Text right */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div className="order-1 lg:order-1">
                            <img
                                src="/usage_2-1280.webp"
                                srcSet="/usage_2-640.webp 640w, /usage_2-1280.webp 1280w"
                                sizes="(min-width: 1024px) 600px, 100vw"
                                width={1280}
                                height={871}
                                alt="Наушники и ноутбук с интерфейсом подкаста"
                                className="w-full h-auto rounded-2xl"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="order-2 lg:order-2">
                            <div className="space-y-4">
                                <p className="onest-description text-gray-600">
                                    Создание подкаста может быть сложным и трудоёмким процессом. От идеи эпизода до публикации контента слушателям — необходимо учесть множество деталей. Именно здесь на помощь приходит SilqAI. Всего несколько кликов — и мы позаботимся о постобработке звука, чтобы вы могли сосредоточиться на самом важном — вашем контенте.
                                </p>
                                <p className="onest-description text-gray-600">
                                    Не нужно тратить часы на изучение нового программного обеспечения или тонкую настройку звука — наши алгоритмы сделают всю работу за вас. Ваш подкаст будет звучать профессионально и безупречно, помогая вам привлечь больше слушателей и увеличить количество ежемесячных скачиваний.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Creation Section */}
            <section className="py-8 sm:py-12 lg:py-16 px-6 sm:px-8 lg:px-32 relative bg-white">
                <div className="container mx-auto max-w-7xl relative z-10">
                    {/* Block 1: Text left, Image right */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start mb-12">
                        <div className="order-2 lg:order-1">
                            <h2 className="onest-heading-main mb-4 sm:mb-6 text-gray-900">
                                Создание видео
                            </h2>
                            <p className="onest-description text-gray-600 mb-6 sm:mb-8">
                                Качественный звук имеет большее значение для успешного видео, чем высококачественное изображение.
                            </p>
                            <h3 className="onest-feature-title text-gray-900 mb-4 sm:mb-6">
                                Мои преимущества как создателя видео:
                            </h3>
                            <div className="space-y-3 sm:space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Устраните шумы и улучшите качество звука в ваших видео, сэкономив время при монтаже.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Соответствуйте всем распространенным аудиотребованиям таких платформ, вещательных компаний и рекламных роликов в один клик</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Сделайте свои видео доступными, добавив транскрипции</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Публикуйте на видеоплатформах, таких как YouTube, и автоматически создавайте версии только со звуком</p>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <img
                                src="/usage_3-1280.webp"
                                srcSet="/usage_3-640.webp 640w, /usage_3-1280.webp 1280w"
                                sizes="(min-width: 1024px) 600px, 100vw"
                                width={1280}
                                height={1129}
                                alt="Видеоплеер YouTube с камерой"
                                className="w-full h-auto rounded-2xl"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>

                    {/* Block 2: Image left, Text right */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div className="order-1 lg:order-1">
                            <img
                                src="/usage_4-1280.webp"
                                srcSet="/usage_4-640.webp 640w, /usage_4-1280.webp 1280w"
                                sizes="(min-width: 1024px) 600px, 100vw"
                                width={1280}
                                height={1056}
                                alt="Женщина за ноутбуком с логотипом SilqAI"
                                className="w-full h-auto rounded-2xl"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="order-2 lg:order-2">
                            <div className="space-y-4">
                                <p className="onest-description text-gray-600">
                                    Когда вы концентрируетесь на идеальном кадре, качество звука в видео часто оставляет желать лучшего. Но плохое качество звука может испортить впечатление от просмотра. Вот тут-то и пригодится SilqAI.
                                </p>
                                <p className="onest-description text-gray-600">
                                    Наш инструмент для постобработки возьмёт на себя тонкую настройку, автоматически гарантируя соответствие всем распространённым аудиотребованиям, рекламных роликов и видеоплатформ.
                                </p>
                                <div className="bg-gray-100 rounded-xl p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center min-w-[32px] min-h-[32px]">
                                            <Zap className="w-4 h-4 text-purple-600" />
                                        </div>
                                        <p className="onest-description text-gray-700">
                                            Так почему бы не доверить SilqAI заботу о звуке, пока вы сосредоточены на других аспектах видеопроизводства?
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Audiobooks Section */}
            <section className="py-8 sm:py-12 lg:py-16 px-6 sm:px-8 lg:px-32 relative bg-white">
                <div className="container mx-auto max-w-7xl relative z-10">
                    {/* Block 1: Text left, Image right */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start mb-12">
                        <div className="order-2 lg:order-1">
                            <h2 className="onest-heading-main mb-4 sm:mb-6 text-gray-900">
                                Аудиокниги
                            </h2>
                            <p className="onest-description text-gray-600 mb-6 sm:mb-8">
                                С SilqAI вы можете создать свою аудиокнигу всего за несколько минут. Создавайте высококачественный контент, который цепляет аудиторию с самого начала.
                            </p>
                            <h3 className="onest-feature-title text-gray-900 mb-4 sm:mb-6">
                                Мои преимущества как создателя аудиокниг
                            </h3>
                            <div className="space-y-3 sm:space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Автоматически соответствовать аудиотребованиям основных платформ, таких как Audible</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Устранение нежелательных шумов и шипения (де-эссер), чтобы создать теплый и приятный голос</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Добавьте обозначения глав, чтобы слушателям было легче ориентироваться в вашем контенте.</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="onest-description text-gray-600">Автоматизируйте процесс публикации с помощью предустановок и бесшовной интеграции</p>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <img
                                src="/usage_5-1280.webp"
                                srcSet="/usage_5-640.webp 640w, /usage_5-1280.webp 1280w"
                                sizes="(min-width: 1024px) 600px, 100vw"
                                width={1280}
                                height={1129}
                                alt="Мужчина в наушниках с музыкальными нотами"
                                className="w-full h-auto rounded-2xl"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>

                    {/* Block 2: Image left, Text right */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div className="order-1 lg:order-1">
                            <img
                                src="/usage_6-1280.webp"
                                srcSet="/usage_6-640.webp 640w, /usage_6-1280.webp 1280w"
                                sizes="(min-width: 1024px) 600px, 100vw"
                                width={1280}
                                height={1184}
                                alt="Наушники на книгах с телефоном"
                                className="w-full h-auto rounded-2xl"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="order-2 lg:order-2">
                            <div className="space-y-4">
                                <p className="onest-description text-gray-600">
                                    Профессиональное производство аудиокниг больше не требует команды звукорежиссёров. С SilqAI вы легко создадите свою следующую высококачественную аудиокнигу.
                                </p>
                                <p className="onest-description text-gray-600">
                                    Наш инструмент постобработки предназначен для редактирования и улучшения записей речи, устранения нежелательных шумов и шипения, сохраняя суть вашего контента. А если у вас есть уже имеющиеся звуковые эффекты, которые вы хотите сохранить, мы не поступимся их качеством.
                                </p>
                                <div className="bg-gray-100 rounded-xl p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center min-w-[32px] min-h-[32px]">
                                            <Music className="w-4 h-4 text-purple-600" />
                                        </div>
                                        <p className="onest-description text-gray-700">
                                            С SilqAI ваша аудиокнига будет иметь безупречное, профессиональное звучание, которое оценят даже самые взыскательные слушатели.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

        </>
    )
}